<script setup lang="ts">
import {
  PhCheckCircle,
  PhSpinnerGap,
  PhWarningCircle,
} from '@phosphor-icons/vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ModalTransition } from '../modal-transition';
import { useFileManagerMailDraft } from '../../logic/core/useFileManagerMailDraft';
import type { MailItemStatus } from '../../logic/tools/outlook-mail/createMailDraft';

const { t } = useI18n();

// Self-inject style, like DeleteConfirmModal and TransferConfirmModal rather than the emit style of
// CopyModal/RenameModal: everything this renders and every action it offers already lives in the
// composable's provided state, so threading props and emits through FileManager.vue would add a layer
// that carries no decisions.
const {
  items,
  phase,
  webLink,
  failureReason,
  canRetry,
  retry,
  cancel,
  dismiss,
} = useFileManagerMailDraft();

const isRunning = computed(() => phase.value === 'running');

const failedCount = computed(
  () => items.value.filter((item) => item.status === 'failed').length,
);
const succeededCount = computed(
  () => items.value.filter((item) => item.status === 'done').length,
);

// One headline per terminal state. 'completed' means the draft EXISTS, which is true even when every
// attachment failed — hence the separate partial-failure line below rather than a fourth headline that
// would have to hedge.
const headline = computed(() => {
  switch (phase.value) {
    case 'running':
      return t('fileManager.mailDraftModal.running');
    case 'completed':
      return t('fileManager.mailDraftModal.completed');
    case 'cancelled':
      return t('fileManager.mailDraftModal.cancelled');
    default:
      return t('fileManager.mailDraftModal.failed');
  }
});

const partialFailureNote = computed(() =>
  phase.value === 'completed' && failedCount.value > 0
    ? t('fileManager.mailDraftModal.partialFailure', {
        succeeded: succeededCount.value,
        failed: failedCount.value,
      })
    : null,
);

const STATUS_LABELS: Record<MailItemStatus, string> = {
  queued: 'fileManager.mailDraftModal.statusQueued',
  downloading: 'fileManager.mailDraftModal.statusDownloading',
  attaching: 'fileManager.mailDraftModal.statusAttaching',
  done: 'fileManager.mailDraftModal.statusDone',
  failed: 'fileManager.mailDraftModal.statusFailed',
};

// A failed row explains itself with the specific reason (403/404/413/429 each read differently); every
// other row shows its phase. Two phases per item is what keeps a multi-file run from ever looking frozen
// — there is deliberately no byte percentage, since download progress cannot be measured without
// incrementally reading the response body.
const statusText = (status: MailItemStatus, reason?: string) =>
  status === 'failed' && reason ? reason : t(STATUS_LABELS[status]);

/**
 * Called DIRECTLY in the click handler — not behind a nextTick, not after an await. That is the entire
 * reason this button exists instead of the app opening Outlook itself once the run finishes: a
 * window.open() many awaits after the original click is silently blocked, while this click is a fresh
 * user gesture.
 */
const openInOutlook = () => {
  if (webLink.value) window.open(webLink.value, '_blank');
  dismiss();
};
</script>

<template>
  <!--
    No `.modal-background` cancel binding and no header `.delete` button while the run is going, unlike
    every other modal in this repo. Deliberate: silent dismissal of a long operation is how you get users
    who do not know whether their mail exists. Escape is unhandled — which is consistent with the rest of
    the repo (no modal here handles it) and correct here.
  -->
  <ModalTransition>
    <div class="modal is-active" role="dialog" aria-modal="true">
      <div class="modal-background" @click="dismiss"></div>

      <div class="modal-card mail-draft-modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ t('fileManager.mailDraftModal.title') }}</p>
          <button
            v-if="!isRunning"
            class="delete"
            :aria-label="t('fileManager.mailDraftModal.closeAriaLabel')"
            type="button"
            @click="dismiss"
          ></button>
        </header>

        <section class="modal-card-body">
          <p class="mail-draft-headline">{{ headline }}</p>
          <p v-if="partialFailureNote" class="mail-draft-note">
            {{ partialFailureNote }}
          </p>
          <p v-if="failureReason" class="mail-draft-note mail-draft-note--danger">
            {{ failureReason }}
          </p>

          <!-- Same three-icons-for-five-statuses vocabulary as the upload progress panel
               (FileExplorerHandler.vue), so the two progress surfaces read as one system. -->
          <ul v-if="items.length" class="mail-draft-list">
            <li v-for="item in items" :key="item.path" class="mail-draft-row">
              <PhCheckCircle
                v-if="item.status === 'done'"
                :size="14"
                class="status-done"
              />
              <PhWarningCircle
                v-else-if="item.status === 'failed'"
                :size="14"
                class="status-failed"
              />
              <PhSpinnerGap v-else :size="14" class="status-pending" />
              <span class="mail-draft-name" :title="item.name">{{ item.name }}</span>
              <span
                class="mail-draft-status"
                :class="{ 'mail-draft-status--danger': item.status === 'failed' }"
              >
                {{ statusText(item.status, item.failureReason) }}
              </span>
            </li>
          </ul>
        </section>

        <footer class="modal-card-foot actions">
          <button
            v-if="isRunning"
            type="button"
            class="button is-light"
            @click="cancel"
          >
            {{ t('fileManager.mailDraftModal.cancel') }}
          </button>
          <template v-else>
            <button type="button" class="button is-light" @click="dismiss">
              {{ t('fileManager.mailDraftModal.close') }}
            </button>
            <button
              v-if="canRetry"
              type="button"
              class="button is-primary"
              @click="retry"
            >
              {{ t('fileManager.mailDraftModal.retry') }}
            </button>
            <button
              v-if="webLink"
              type="button"
              class="button is-primary"
              @click="openInOutlook"
            >
              {{ t('fileManager.mailDraftModal.openInOutlook') }}
            </button>
          </template>
        </footer>
      </div>
    </div>
  </ModalTransition>
</template>

<style scoped lang="scss">
.mail-draft-modal-card {
  width: min(480px, 92vw);

  .modal-card-head,
  .modal-card-foot {
    padding: 0.75rem 1.5rem;
  }

  .modal-card-title {
    font-size: 1.1rem;
  }

  .delete {
    height: 24px;
    width: 24px;
    max-height: 24px;
    max-width: 24px;
  }
}

.actions {
  justify-content: flex-end;
  gap: 1rem;
}

.mail-draft-headline {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
}

.mail-draft-note {
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.mail-draft-note--danger {
  color: var(--text-danger);
}

/* Capped height + scroll rather than letting the modal grow unbounded, so a large selection still fits
   on screen instead of pushing the footer off it — same treatment as DeleteConfirmModal's target list. */
.mail-draft-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  max-height: 220px;
  margin-top: 0.75rem;
  padding: 0.5rem;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0.5rem;
}

.mail-draft-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.mail-draft-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mail-draft-status {
  flex-shrink: 0;
  max-width: 55%;
  overflow: hidden;
  color: #6b7280;
  font-size: 0.75rem;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mail-draft-status--danger {
  color: var(--text-danger);
}

/* Same colors and the same spin the upload progress panel uses. */
.status-done {
  color: #2e9e5b;
  flex-shrink: 0;
}

.status-failed {
  color: #d1453b;
  flex-shrink: 0;
}

.status-pending {
  color: #999;
  flex-shrink: 0;
  animation: mail-draft-spin 1s linear infinite;
}

@keyframes mail-draft-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
